import { useEffect, useState } from "react";
import { fetchAssignables, fetchPCC, updatePCCAssigned, updatePCCPriority, updatePCCStatus } from "../../apis/pccApi";
import { message, Table, Checkbox, Input } from "antd";
import { Link } from 'react-router-dom';
import PCCCard from "./PCCCard";
import PCCComments from "./PCCComments";
import PCCStatusHistory from "./PCCStatusHistory";
import PCCAssignedHistory from "./PCCAssignedHistory";
import moment from "moment";


const PCCDashboard = () => {
  const [pqrs, setPqrs] = useState([]);
  const [expandedPqrIds, setExpandedPqrIds] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [searchedID, setSearchedID] = useState("");
  const [searchedUser, setSearchedUser] = useState("");
  const [searchedAssigned, setSearchedAssigned] = useState("");

  useEffect(() => {
    const getPcc = async () => {
      try {
        const response = await fetchPCC();
        setPqrs(response.data);
      } catch (error) {
        message.error('Error al cargar los PQRs, contacta a servicios técnicos');
      }
    };
    getPcc();
  }, []);

  useEffect(() => {
    const loadAssignables = async () => {
      try{
        const response = await fetchAssignables();
        setAssignables(response.data);
      } catch (error) {
        setAssignables([])
        message.error('Error al cargar los asignables, contacta a servicios técnicos');
      }
    };
    loadAssignables();
  }, []);

  const toggleExpand = (pqrId) => {
    setExpandedPqrIds((prev) =>
      prev.includes(pqrId) ? prev.filter((id) => id !== pqrId) : [...prev, pqrId]
    );
  };

  const handlePriorityChange = async (pccId, newPriority) => {
    try {
      const parsedPriority = parseInt(newPriority);
      await updatePCCPriority(pccId, parsedPriority);

      // Update local state so UI reflects the change
      setPqrs((prevPqrs) =>
        prevPqrs.map((pqr) =>
          pqr.id === pccId ? { ...pqr, priority: parsedPriority } : pqr
        )
      );

      message.success("Prioridad actualizada con éxito!");
    } catch (error) {
      message.error("Error actualizando la prioridad, intenta más tarde");
    }
  };

  const handleStatusChange = async (pccId, newStatus) => {
    try {
      const parsedStatus = parseInt(newStatus);
      await updatePCCStatus(pccId, parsedStatus);

      // Update local state so UI reflects the change
      setPqrs((prevPqrs) =>
        prevPqrs.map((pqr) =>
          pqr.id === pccId ? { ...pqr, status: parsedStatus } : pqr
        )
      );

      message.success("Status actualizado con éxito!");
    } catch (error) {
      message.error("Error actualizando el Status, intenta más tarde");
    }
  };

  const handleAssignedChange = async (pccId, newAssigned) => {
    try {
      await updatePCCAssigned(pccId, newAssigned);

      // Update local state so UI reflects the change
      setPqrs((prevPqrs) =>
        prevPqrs.map((pqr) =>
          pqr.id === pccId ? { ...pqr, assigned_to: newAssigned } : pqr
        )
      );

      message.success("Encargado actualizado con éxito!");
    } catch (error) {
      message.error("Error actualizando el encargado, intenta más tarde");
    }
  };

  // Filter closed PQRs (status === 1)
  let closedPqrs = pqrs.filter(pqr => pqr.status === 1);

  // Apply search filters
  closedPqrs = closedPqrs.filter(pqr => 
    searchedID ? String(pqr.id)===searchedID : true &&
    pqr.reporter_name?.toLowerCase().includes(searchedUser.toLowerCase()) &&
    (assignables.find(a => a.id === pqr.assigned_to)?.name || "")
      .toLowerCase().includes(searchedAssigned.toLowerCase())
  );

  // Table columns for closed PQRs
  const columns = [
    { title: (
      <div>
        <p>ID</p>
        <Input placeholder="Buscar ID" value={searchedID} onChange={e => setSearchedID(e.target.value)} />
      </div>
    ),
    dataIndex: "id",
    key: "id",
    align: "center",
  },
    { title: (
      <div>
        <p>Usuario</p>
        <Input placeholder="Buscar usuairo" value={searchedUser} onChange={e => setSearchedUser(e.target.value)} />
      </div>
      ),
      dataIndex: "reporter_name",
      key: "reporter_name",
      align: "center",
      render: (text, record) => (
        <Link to={`/users/${record.user_id}`}>
          {`${record.reporter_name}`}
        </Link>
      )
    },
    {
      title: (
        <div>
        <p>Último Encargado</p>
        <Input placeholder="Buscar encargado" value={searchedAssigned} onChange={e => setSearchedAssigned(e.target.value)} />
      </div>
      ),
      dataIndex: "assigned_to",
      key: "assigned_to",
      align: "center",
      render: (assignedId) => {
        const assigned = assignables.find(a => a.id === assignedId);
        return assigned ? assigned.name : assignedId;
      },
    },
    {
      title: "Fecha Cierre",
      dataIndex: "updated_at",
      key: "updated_at",
      align: "center",
      render: (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : ""
    },
  ];

  return (
    <div className="container mt-4">
      <h2 className="mb-4">PCC System Dashboard</h2>

      <div style={{ marginBottom: 24 }}>
        <Checkbox
          checked={includeClosed}
          onChange={e => setIncludeClosed(e.target.checked)}
        >
          Ver PQRs cerrados
        </Checkbox>
      </div>

      {includeClosed && (
        <div style={{ marginBottom: 32 }}>
          <h4>PQRs Cerrados</h4>
          <Table
            dataSource={closedPqrs}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            expandable={{
              expandedRowRender: (record) => (
                <>
                  <PCCCard
                    key={record.id}
                    pqr={record}
                    assignables={assignables}
                    onSelect={() => toggleExpand(record.id)}
                    onPriorityChange={handlePriorityChange}
                    onStatusChange={handleStatusChange}
                    onAssignedChange={handleAssignedChange}
                  />
                  {expandedPqrIds.includes(record.id) && (
                    <div className="ms-4 mb-4">
                      <PCCComments pqrId={record.id} />
                      <PCCStatusHistory pqrId={record.id} />
                      <PCCAssignedHistory pqrId={record.id} />
                    </div>
                  )}
                </>
              ),
              rowExpandable: (record) => true,
            }}
          />
        </div>
      )}

      {pqrs
        .filter(pqr => pqr.status !== 1)
        .map((pqr) => (
        <div key={pqr.id}>
          <PCCCard
            key={pqr.id}
            pqr={pqr}
            assignables={assignables}
            onSelect={() => toggleExpand(pqr.id)}
            onPriorityChange={handlePriorityChange}
            onStatusChange={handleStatusChange}
            onAssignedChange={handleAssignedChange}
          />
          {expandedPqrIds.includes(pqr.id) && (
            <div className="ms-4 mb-4">
              <PCCComments pqrId={pqr.id} />
              <PCCStatusHistory pqrId={pqr.id} />
              <PCCAssignedHistory pqrId={pqr.id} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PCCDashboard;
